// Angular modules.
import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
	ViewChild,
} from '@angular/core';
// Third-party modules.
import { TranslateService } from '@ngx-translate/core';
// Zenklub enums.
import { ESidebarMode } from 'app/enums/sidebarmode.enum';
import { IBusySlot } from 'app/interfaces/availability.interface';
// Zenklub interfaces.
import { IDay } from 'app/interfaces/calendar.interface';
import { ICalendarEvent } from 'app/interfaces/event.interface';
import { ISidebar } from 'app/interfaces/sidebar.interface';
import { AvailabilitiesService } from 'app/services/availabilities.service';
// Zenklub services.
import { CalendarService } from 'app/services/calendar.service';
import { DateTimeService } from 'app/services/datetime.service';
import { EventsService } from 'app/services/events.service';
import { GlobalService } from 'app/services/global.service';
import { tableAnimation } from 'app/utils/animations.util';
import { Subscription } from 'rxjs';

@Component({
	selector: 'app-monthcalendar',
	templateUrl: './monthcalendar.component.html',
	styleUrls: ['./monthcalendar.component.scss'],
	animations: [tableAnimation],
})
export class MonthcalendarComponent implements OnChanges, OnInit {
	// Inputs.
	@Input() activeDate: IDay;
	@Input() firstCall: boolean;
	@Input() forceRefreshEvents: boolean;
	// Outputs.
	@Output() showDayEvents: EventEmitter<ISidebar> =
		new EventEmitter<ISidebar>();
	@Output() confirmRefreshEvents: EventEmitter<boolean> =
		new EventEmitter<boolean>();
	arDays: any[] = [];
	activeDayOfWeek: number;
	// Today date.
	today: Date;
	// Hold last viewed month and year.
	lastMonth: number;
	lastYear: number;
	// Hold events.
	eventsList: ICalendarEvent[] = [];
	busySlots: IBusySlot[] = [];
	// Labels.
	strMoreItems: string;
	strPending: string;
	// Status.
	currentMonthVisible = false;
	eventsListed = false;
	isLoading = false;
	isMobile = false;
	// Listener for new events.
	newEvSubscription: Subscription;
	// Constructor method.

	@ViewChild('faIcon') faIcon;

	constructor(
		private calendar: CalendarService,
		private dateTime: DateTimeService,
		private events: EventsService,
		private global: GlobalService,
		private translate: TranslateService,
		private availabilityService: AvailabilitiesService
	) {}

	// On init.
	ngOnInit() {
		if (this.firstCall) {
			this.firstCall = false;
			const d: Date = new Date();
			this.showEventsOfDay(d.getDate(), d.getMonth());
		}
		// Today.
		this.today = new Date();
		this.today.setHours(0, 0, 0, 0);
		// Current day of week.
		this.activeDayOfWeek = new Date().getDay();
		// Get translation for labels.
		this.translate.get(['GENERAL.MORE', 'GENERAL.PENDING']).subscribe((res) => {
			this.strMoreItems = res['GENERAL.MORE'];
			this.strPending = res['GENERAL.PENDING'];
			// Update events list.
			if (!this.eventsListed) {
				this.getEvents();
				this.eventsListed = true;
			}
		});
		// Set sizes and positions.
		this.windowResized();
		// Subscription for new Events.
		this.events.eventEmitter().subscribe((event) => this.updateNewEvent(event));
	}

	// On changes.
	ngOnChanges() {
		if (
			this.activeDate.year !== this.lastYear ||
			this.activeDate.month !== this.lastMonth
		) {
			// Get grid content.
			this.arDays = this.calendar.createCalendarGrid(this.activeDate.date);
			// Update last viewed month.
			this.lastMonth = this.activeDate.month;
			this.lastYear = this.activeDate.year;
			// Set as not listed.
			this.eventsList = [];
			this.eventsListed = false;
		}
		// Get events from period.
		if (!this.eventsListed || this.forceRefreshEvents) {
			// Update events list.
			this.eventsList = [];
			this.eventsListed = true;
			this.getEvents();
			// Updated events.
			if (this.forceRefreshEvents) {
				this.forceRefreshEvents = false;
				this.confirmRefreshEvents.emit(false);
			}
		}
		this.currentMonthVisible = this.currentMonthIsVisible();
	}

	// Set elements size and position.
	public windowResized(): void {
		// Verify if is mobile like device.
		this.isMobile = this.global.isMobileDevice();
	}

	// Get events.
	private getEvents(
		doScroll = true,
		showLoader = true,
		forceReload = false
	): void {
		// Loading.
		this.isLoading = showLoader;
		// New items?
		let newItems = false;
		// Get first and last days of current month.
		const year: number = this.activeDate.year;
		const month: number = this.activeDate.month - 1;
		const firstDay = new Date(year, month, 1);
		const lastDay = new Date(year, month, this.calendar.daysInMonth(firstDay));
		// Get events.
		this.events.getEvents(firstDay, lastDay).subscribe((result) => {
			if (this.eventsList.length === 0) {
				this.eventsList = result;
				this.isLoading = false;
				newItems = true;
			} else {
				result.forEach((item) => {
					if (!this.eventsList.some((event) => event.id === item.id)) {
						this.eventsList.push(item);
						newItems = true;
					}
				});
			}
			// get the busySlots
			this.getNewBusySlots();
			if (newItems || forceReload || this.eventsList.length !== result.length) {
				setTimeout(() => {
					this.showEvents(doScroll);
				}, 0);
			}
		});
		this.isLoading = false;
	}

	private getNewBusySlots(doScroll = true, forceReload = false): void {
		// Get first and last days of current month.
		const year: number = this.activeDate.year;
		const month: number = this.activeDate.month - 1;
		const firstDay = new Date(year, month, 1);
		const lastDay = new Date(year, month, this.calendar.daysInMonth(firstDay));
		let newItems = false;
		// Get events.
		this.availabilityService.getBusySlots(firstDay, lastDay).then((result) => {
			if (this.busySlots.length === 0) {
				this.busySlots = result;
				this.isLoading = false;
				newItems = true;
			} else {
				result.forEach((item) => {
					if (!this.busySlots.some((event) => event.id === item.id)) {
						this.busySlots.push(item);
						newItems = true;
					}
				});
			}
			if (newItems || forceReload || this.busySlots.length !== result.length) {
				setTimeout(() => {
					this.showBusySlots(doScroll);
				}, 0);
			}
		});
	}

	private updateNewEvent(item: ICalendarEvent) {
		const idx = this.eventsList.findIndex((e) => item.id === e.id);
		if (idx !== -1) {
			if (item.status === 'cancelled') {
				this.eventsList.splice(idx, 1);
			} else {
				// just update the event;
				this.eventsList[idx] = item;
			}
		} else {
			this.eventsList.push(item);
		}
		setTimeout(() => {
			this.showEvents(true);
		}, 0);
	}

	// Show events on calendar.
	public showBusySlots(doScroll: boolean): void {
		if (this.busySlots.length > 0) {
			const items: IBusySlot[] = this.busySlots;
			// Store total day events (mobile only).
			for (let i = 0; i < items.length; i++) {
				let elContent: HTMLElement;
				const item: IBusySlot = items[i];
				const itemDate: Date = item.startDate;

				const elChild: JQuery<HTMLElement> = $(
					'div[data-day="' +
						itemDate.getDate() +
						'"][data-month="' +
						itemDate.getMonth() +
						'"]'
				);
				if (elChild) {
					const element: any = elChild.parent();
					if (element && element.length > 0) {
						for (let x = 0; x < element[0].childNodes.length; x++) {
							const el: HTMLElement = element[0].childNodes[x];
							if (el.classList && el.classList.contains('day-content')) {
								elContent = el;
							}
						}
					}
					// Loaded
					this.isLoading = false;
					const content: HTMLDivElement = document.createElement(
						'div'
					) as HTMLDivElement;
					const type = 'personal-session';
					const name: string = item.description ? item.description : '';
					const hours: string = this.dateTime.getFormatedHours(itemDate);
					// Total items of a day.
					const totItems: number =
						elContent && elContent.children ? elContent.children.length : 0;
					const moreText: string = this.strMoreItems.toLowerCase();
					if (totItems > 4 && elContent) {
						content.innerHTML =
							'<div style="display:none;"><div class="divMonthEvent col-md-9"></div></div>';
						elContent.appendChild(content);
						const elTotal: any = elContent.childNodes[4];
						elTotal.innerHTML =
							'<div><div class="divMonthEvent moreDayEvents col-md-9">' +
							(totItems - 3) +
							' ' +
							moreText +
							'</div></div>';
					} else if (totItems === 4 && elContent) {
						content.innerHTML =
							'<div><div class="divMonthEvent moreDayEvents col-md-9">1 ' +
							moreText +
							'</div></div>';
						elContent.appendChild(content);
					} else if (totItems < 4 && elContent) {
						const icon =
							this.faIcon?.renderedIconHTML
								?.changingThisBreaksApplicationSecurity;

						content.innerHTML = `
						<div>
							<div class="divMonthEvent col-md-9" (click)="showEventsOfDay(${itemDate})">
								<fa-icon icon="circle" class="${type}">${icon}</fa-icon>
								<span>${name}</span>
							</div>
							<div class="col-md-3 hour">${hours}</div>
						</div>`;

						elContent.appendChild(content);
					}
				}
			}
			// Scroll to proper row.
			if (doScroll && !this.isMobile) {
				setTimeout(() => {
					if (this.today.getMonth() + 1 === this.activeDate.month) {
						const currentRow: Element = document.querySelector(
							'[data-day="' + this.activeDate.day + '"]'
						);
						if (currentRow) {
							currentRow.scrollIntoView({ block: 'start', behavior: 'smooth' });
						}
					}
				}, 0);
			}
		}
	}

	// Show events on calendar.
	public showEvents(doScroll: boolean): void {
		if (this.eventsList.length > 0) {
			const items: ICalendarEvent[] = this.eventsList;
			$('.day-content').html('');
			// Store total day events (mobile only).
			for (let i = 0; i < items.length; i++) {
				if (items[i].status !== 'cancelled') {
					let elContent: HTMLElement;
					const item: ICalendarEvent = items[i];
					const itemDate: Date = new Date(item.day);

					const elChild: JQuery<HTMLElement> = $(
						'div[data-day="' +
							itemDate.getDate() +
							'"][data-month="' +
							itemDate.getMonth() +
							'"]'
					);
					if (elChild) {
						const element: any = elChild.parent();
						if (element && element.length > 0) {
							for (let x = 0; x < element[0].childNodes.length; x++) {
								const el: HTMLElement = element[0].childNodes[x];
								if (el.classList && el.classList.contains('day-content')) {
									elContent = el;
								}
							}
							// Loaded
							this.isLoading = false;

							const content: HTMLDivElement = document.createElement(
								'div'
							) as HTMLDivElement;
							const type: string =
								item.status === 'paid' &&
								item.contactReason &&
								item.contactReason.length === 0
									? 'no-contact-reason'
									: 'online-session';

							const name: string =
								item.status !== 'reserved' ? item.userName : this.strPending;
							const hours: string = this.dateTime.getFormatedHours(itemDate);
							// Total items of a day.
							const totItems: number =
								elContent && elContent.children ? elContent.children.length : 0;
							const moreText: string = this.strMoreItems.toLowerCase();
							if (totItems > 4) {
								content.innerHTML =
									'<div style="display:none;"><div class="divMonthEvent col-md-9"></div></div>';
								elContent.appendChild(content);
								const elTotal: any = elContent.childNodes[4];
								elTotal.innerHTML =
									'<div><div class="divMonthEvent moreDayEvents col-md-9">' +
									(totItems - 3) +
									' ' +
									moreText +
									'</div></div>';
							} else if (totItems === 4) {
								content.innerHTML =
									'<div><div class="divMonthEvent moreDayEvents col-md-9">1 ' +
									moreText +
									'</div></div>';
								elContent.appendChild(content);
							} else if (totItems < 4) {
								const isPresentialAppointment = !!item.professionalAddress;

								const icon =
									this.faIcon?.renderedIconHTML
										?.changingThisBreaksApplicationSecurity;

								const circleIcon =
									(item.status === 'invite' && item.origin === 'invite') ||
									item.status === 'reschedule_request'
										? `<span class="freud-icon freud-icon-circle ${
												isPresentialAppointment ? 'face-to-face' : 'online'
										  }"></span>`
										: `<fa-icon icon="circle" class="${type}">${icon}</fa-icon>`;

								content.innerHTML = `
								<div>
									<div class="divMonthEvent col-md-9" (click)="showEventsOfDay(${itemDate})">
										${circleIcon}
										${
											item.status === 'invite'
												? '<span class="freud-icon freud-icon-send"></span>'
												: ''
										}
										<span>${name}</span>
									</div>
									<div class="col-md-3 hour">${hours}</div>
								</div>`;

								elContent.appendChild(content);
							}
						}
					}
				}
			}

			// Scroll to proper row.
			if (doScroll && !this.isMobile) {
				setTimeout(() => {
					if (this.today.getMonth() + 1 === this.activeDate.month) {
						const currentRow: Element = document.querySelector(
							'[data-day="' + this.activeDate.day + '"]'
						);
						if (currentRow) {
							currentRow.scrollIntoView({ block: 'start', behavior: 'smooth' });
						}
					}
				}, 0);
			}
		}
	}

	// Show events of a day.
	public showEventsOfDay(day: number, month: number): void {
		// Build date.
		const curY: number = this.activeDate.year;
		const curM: number = month;
		const date: Date = new Date(curY, curM, day);
		// Create sidebar info.
		const sidebar: ISidebar = {
			date: date,
			id: null,
			mode: ESidebarMode.Appointments,
		};
		// Show details.
		this.showDayEvents.emit(sidebar);
	}

	// Verify if current month is visible.
	private currentMonthIsVisible(): boolean {
		return this.activeDate.month - 1 === new Date().getMonth();
	}
}
